// SMSF Fee Engine - Comprehensive SMSF fee calculation and management
// Integrates with existing SimulationEngine fee system
// Last updated: September 2025

import { 
    FinancialConfig, 
    calculateSMSFTrusteeFees, 
    calculateSMSFAuditCosts, 
    calculateSMSFComplianceCosts,
    calculateSMSFInvestmentFees,
    calculateSMSFInsuranceCosts,
    calculateSMSFValuationCosts,
    calculateTotalSMSFCosts
} from './FinancialConfig.js';

export const SMSFFeeEngine = {
    // SMSF Trustee Fee Manager
    TrusteeFeeManager: {
        // Calculate comprehensive trustee fees
        calculateTrusteeFees(smsfBalance, trusteeType = 'individual', memberCount = 2, complexity = 'standard') {
            const baseFees = calculateSMSFTrusteeFees(smsfBalance, trusteeType);
            
            // Adjust for member count (more members = more complexity)
            const memberMultiplier = Math.min(2.0, 1 + (memberCount - 2) * 0.1);
            
            // Adjust for complexity
            const complexityMultiplier = complexity === 'complex' ? 1.5 : 1.0;
            
            const adjustedFees = baseFees * memberMultiplier * complexityMultiplier;
            
            return {
                baseFees,
                memberCount,
                complexity,
                memberMultiplier,
                complexityMultiplier,
                totalFees: Math.round(adjustedFees),
                breakdown: {
                    baseFee: baseFees,
                    memberAdjustment: baseFees * (memberMultiplier - 1),
                    complexityAdjustment: baseFees * memberMultiplier * (complexityMultiplier - 1)
                }
            };
        },

        // Compare trustee fee options
        compareTrusteeOptions(smsfBalance, memberCount = 2, complexity = 'standard') {
            const individual = this.calculateTrusteeFees(smsfBalance, 'individual', memberCount, complexity);
            const corporate = this.calculateTrusteeFees(smsfBalance, 'corporate', memberCount, complexity);
            
            return {
                individual,
                corporate,
                recommendation: corporate.totalFees < individual.totalFees ? 'corporate' : 'individual',
                savings: Math.abs(corporate.totalFees - individual.totalFees),
                savingsPercentage: Math.abs(corporate.totalFees - individual.totalFees) / Math.max(individual.totalFees, corporate.totalFees) * 100
            };
        }
    },

    // SMSF Audit Cost Manager
    AuditCostManager: {
        // Calculate comprehensive audit costs
        calculateAuditCosts(complexity = 'standard', assetTypes = {}, memberCount = 2, hasLRBA = false) {
            const baseAuditCost = calculateSMSFAuditCosts(complexity);
            
            // Adjust for asset complexity
            let assetComplexityMultiplier = 1.0;
            if (assetTypes.collectibles > 0) assetComplexityMultiplier += 0.3;
            if (assetTypes.property > 0) assetComplexityMultiplier += 0.2;
            if (assetTypes.alternativeInvestments > 0) assetComplexityMultiplier += 0.4;
            
            // Adjust for LRBA
            if (hasLRBA) assetComplexityMultiplier += 0.3;
            
            // Adjust for member count
            const memberMultiplier = Math.min(1.5, 1 + (memberCount - 2) * 0.1);
            
            const totalAuditCost = Math.round(baseAuditCost * assetComplexityMultiplier * memberMultiplier);
            
            return {
                baseAuditCost,
                assetComplexityMultiplier,
                memberMultiplier,
                totalAuditCost,
                breakdown: {
                    baseCost: baseAuditCost,
                    assetComplexityAdjustment: baseAuditCost * (assetComplexityMultiplier - 1),
                    memberAdjustment: baseAuditCost * assetComplexityMultiplier * (memberMultiplier - 1)
                }
            };
        }
    },

    // SMSF Compliance Cost Manager
    ComplianceCostManager: {
        // Calculate comprehensive compliance costs
        calculateComplianceCosts(complexity = 'standard', hasLRBA = false, memberCount = 2, assetTypes = {}) {
            const baseComplianceCosts = calculateSMSFComplianceCosts();
            
            // Additional costs for complexity
            let additionalCosts = 0;
            
            // LRBA compliance costs
            if (hasLRBA) {
                additionalCosts += 500; // Additional LRBA compliance documentation
            }
            
            // Asset-specific compliance costs
            if (assetTypes.collectibles > 0) {
                additionalCosts += 300; // Collectibles compliance documentation
            }
            
            if (assetTypes.property > 0) {
                additionalCosts += 400; // Property compliance documentation
            }
            
            // Member count adjustment
            const memberMultiplier = Math.min(1.3, 1 + (memberCount - 2) * 0.05);
            
            // Complexity multiplier
            const complexityMultiplier = complexity === 'complex' ? 1.4 : 1.0;
            
            const totalComplianceCosts = Math.round((baseComplianceCosts + additionalCosts) * memberMultiplier * complexityMultiplier);
            
            return {
                baseComplianceCosts,
                additionalCosts,
                memberMultiplier,
                complexityMultiplier,
                totalComplianceCosts,
                breakdown: {
                    annualReturn: FinancialConfig.SMSF.FEES.COMPLIANCE_COSTS.ANNUAL_RETURN,
                    bookkeeping: FinancialConfig.SMSF.FEES.COMPLIANCE_COSTS.BOOKKEEPING,
                    administration: FinancialConfig.SMSF.FEES.COMPLIANCE_COSTS.ADMINISTRATION,
                    softwareLicenses: FinancialConfig.SMSF.FEES.COMPLIANCE_COSTS.SOFTWARE_LICENSES,
                    lrbaCompliance: hasLRBA ? 500 : 0,
                    collectiblesCompliance: assetTypes.collectibles > 0 ? 300 : 0,
                    propertyCompliance: assetTypes.property > 0 ? 400 : 0
                }
            };
        }
    },

    // SMSF Investment Fee Manager
    InvestmentFeeManager: {
        // Calculate comprehensive investment management fees
        calculateInvestmentFees(assetAllocation, transactionVolumes = {}, rebalancingFrequency = 'annual') {
            let totalFees = 0;
            const feeBreakdown = {};
            
            // Calculate fees for each asset type
            Object.entries(assetAllocation).forEach(([assetType, balance]) => {
                if (balance > 0) {
                    const transactionVolume = transactionVolumes[assetType] || 0;
                    const fees = calculateSMSFInvestmentFees(assetType, balance, transactionVolume);
                    
                    totalFees += fees;
                    feeBreakdown[assetType] = {
                        balance,
                        transactionVolume,
                        fees: Math.round(fees),
                        feeAsPercentage: (fees / balance * 100).toFixed(3)
                    };
                }
            });
            
            // Add rebalancing costs
            const rebalancingCosts = this.calculateRebalancingCosts(assetAllocation, rebalancingFrequency);
            totalFees += rebalancingCosts;
            
            return {
                totalFees: Math.round(totalFees),
                feeBreakdown,
                rebalancingCosts,
                totalAssets: Object.values(assetAllocation).reduce((sum, balance) => sum + balance, 0),
                overallFeePercentage: (totalFees / Object.values(assetAllocation).reduce((sum, balance) => sum + balance, 0) * 100).toFixed(3)
            };
        },

        // Calculate rebalancing costs
        calculateRebalancingCosts(assetAllocation, frequency = 'annual') {
            const totalAssets = Object.values(assetAllocation).reduce((sum, balance) => sum + balance, 0);
            
            // Estimate rebalancing costs based on frequency and asset mix
            let rebalancingRate = 0;
            
            switch (frequency.toLowerCase()) {
                case 'monthly':
                    rebalancingRate = 0.0005; // 0.05% monthly
                    break;
                case 'quarterly':
                    rebalancingRate = 0.001; // 0.1% quarterly
                    break;
                case 'annual':
                default:
                    rebalancingRate = 0.002; // 0.2% annually
                    break;
            }
            
            return totalAssets * rebalancingRate;
        }
    },

    // SMSF Total Cost Calculator - Integration with existing system
    TotalCostCalculator: {
        // Calculate comprehensive SMSF costs
        calculateTotalSMSFCosts(smsfData) {
            const {
                balance,
                trusteeType = 'individual',
                memberCount = 2,
                complexity = 'standard',
                assetAllocation = {},
                assetTypes = {},
                transactionVolumes = {},
                members = [],
                insuranceNeeds = {},
                hasLRBA = false,
                valuationFrequency = 'annual'
            } = smsfData;
            
            // Calculate each cost component
            const trusteeFees = SMSFFeeEngine.TrusteeFeeManager.calculateTrusteeFees(
                balance, trusteeType, memberCount, complexity
            );
            
            const auditCosts = SMSFFeeEngine.AuditCostManager.calculateAuditCosts(
                complexity, assetTypes, memberCount, hasLRBA
            );
            
            const complianceCosts = SMSFFeeEngine.ComplianceCostManager.calculateComplianceCosts(
                complexity, hasLRBA, memberCount, assetTypes
            );
            
            const investmentFees = SMSFFeeEngine.InvestmentFeeManager.calculateInvestmentFees(
                assetAllocation, transactionVolumes
            );
            
            // Calculate totals
            const totalAnnualCosts = 
                trusteeFees.totalFees +
                auditCosts.totalAuditCost +
                complianceCosts.totalComplianceCosts +
                investmentFees.totalFees;
            
            return {
                totalAnnualCosts: Math.round(totalAnnualCosts),
                costAsPercentage: (totalAnnualCosts / balance * 100).toFixed(3),
                breakdown: {
                    trusteeFees: trusteeFees.totalFees,
                    auditCosts: auditCosts.totalAuditCost,
                    complianceCosts: complianceCosts.totalComplianceCosts,
                    investmentFees: investmentFees.totalFees
                },
                detailedBreakdown: {
                    trusteeFees,
                    auditCosts,
                    complianceCosts,
                    investmentFees
                }
            };
        }
    }
};
